import { ForwardRefRenderFunction, forwardRef } from "react";
import { startOfMonth } from "date-fns";
//#region redux
//#endregion
//#region types
import { InvoiceFormTypes, MissingDocumentFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { MissingDocumentSelectionFormContainerProps } from "models/propTypes/missingDocumentPropTypes";
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { DimensionEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { TitledContainerInner, useDimensionMaps } from "components/common";
import MissingDocumentTodoSelectionForm from "./todoSelectionForm";
import { useMissingDocumentService } from "serviceHooks";
import useTodoFormValidationSchema from "./useTodoFormValidationSchema";
import { ManualConversation } from "components/common/comments";
//#endregion
import { DimensionHelper, LocalizationHelperInstance } from "utils/helpers";

// Rows are not supported for missing documents, so the invoice gets at most one range. The month pair is the
// invoice's envelope and always goes along; the date pair only for a date-based range.
const periodisationFields = (
  periodisation?: InvoiceFormTypes.InvoicePeriodisation
): Pick<
  ApiModel.IncomingInvoicePutInfo,
  "periodisationStart" | "periodisationEnd" | "periodisationStartDate" | "periodisationEndDate"
> => {
  const start = periodisation?.range?.start;
  const end = periodisation?.range?.end;
  const isDate = periodisation?.mode === "date";
  return {
    // startOfMonth also drops the time: an end-of-day 23:59:59.999 would round up to the 2nd in SQL datetime.
    periodisationStart: start ? startOfMonth(start) : undefined,
    periodisationEnd: end ? startOfMonth(end) : undefined,
    periodisationStartDate: isDate ? start : undefined,
    periodisationEndDate: isDate ? end : undefined,
  };
};

const MissingDocumentTodoSelectionFormContainer: ForwardRefRenderFunction<
  FormHandles,
  MissingDocumentSelectionFormContainerProps
> = (props, ref) => {
  const missingDocumentService = useMissingDocumentService();

  const { missingDocument, onFilesUploadChange, oldAttachments } = props;

  const { dimensions: recieptDimentions, forcedDimensionMap: receiptForcedDimensionMap } = useDimensionMaps(
    DimensionEnums.DimensionCategory.Receipt
  );
  const { dimensions: invoiceDimentions, forcedDimensionMap: invoiceForcedDimensionMap } = useDimensionMaps(
    DimensionEnums.DimensionCategory.IncomingInvoice
  );

  const initialValues: MissingDocumentFormTypes.Todo = {
    explanation: "",
    filesToUpload: [],
    postWithoutPicture: false,
    type: missingDocument.missingDocumentType,
    receipt: {
      date: missingDocument.date,
    },
  };

  const validationSchema = useTodoFormValidationSchema(missingDocument, {
    receiptForcedDimensionMap: receiptForcedDimensionMap,
    invoiceForcedDimensionMap: invoiceForcedDimensionMap,
    recieptDimensions: recieptDimentions,
    invoiceDimensions: invoiceDimentions,
  });

  const onMissingDocumentSubmit = async (values: MissingDocumentFormTypes.Todo) => {
    if (!values) return;
    const type = values.type;

    const filesToSubmit = values.filesToUpload.filter((f) => !f.skipOnFormSubmit);

    const missingdocumentPut: ApiModel.MissingDocumentPut = {
      id: missingDocument.id,
      explanation: values.explanation,
      bookWithoutImage: values.postWithoutPicture || (!!missingDocument.attachments?.length && !filesToSubmit.length),
      attachments: filesToSubmit.map((f) => {
        return { contentType: f.contentType, data: f.data, fileName: f.fileName } as ApiModel.MissingDocumentAttachment;
      }),
      missingDocumentType: type,
      dimensionList: DimensionHelper.getDimensionListFromMissingDocumentTodoForm(values),
    };

    if (type === ApiModel.MissingDocumentPutType.Receipt)
      missingdocumentPut.receiptInfo = {
        receiptCategoryId: values.receipt?.category,
        persons: values.receipt?.persons?.map((p) => {
          return {
            company: p.company,
            name: p.name,
          } as ApiModel.ReceiptPerson;
        }),
        intent: values.receipt?.intent,
        currency: values.currency,
      };

    if (type === ApiModel.MissingDocumentPutType.IncomingInvoice) {
      missingdocumentPut.incomingInvoiceInfo = {
        accountNumber: (values.incomingInvoice?.category?.id as number) ?? 0,
        ...periodisationFields(values.incomingInvoice?.periodisation),
      };
    }

    return missingDocumentService.submit(missingDocument.id, missingdocumentPut);
  };

  return (
    <Grid container rowGap={1.25} marginTop={1.25}>
      <ManualConversation parentId={missingDocument.id} commentType={ApiModel.GenericCommentType.MissingDocument} />
      <TitledContainerInner
        subHeader={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.MissingDocument.AddFiles)}
      >
        <MissingDocumentTodoSelectionForm
          ref={ref}
          {...props}
          validationSchema={validationSchema}
          defaultValues={initialValues}
          oldAttachments={oldAttachments}
          handleSubmit={onMissingDocumentSubmit}
          hasReceiptDimensions={!!recieptDimentions?.length}
          requiredReceiptDimensions={!!receiptForcedDimensionMap?.length}
          hasIncomingInvoiceDimensions={!!invoiceDimentions?.length}
          onFilesUploadChange={onFilesUploadChange}
          fileViewer={props.fileViewer}
        />
      </TitledContainerInner>
    </Grid>
  );
};

export default forwardRef(MissingDocumentTodoSelectionFormContainer);
